import type { Voice } from "@/types/vapi-voices";

export interface VoiceOption {
  voice_id: string;
  name: string;
  previewUrl?: string | null;
  is_custom?: boolean;
  description?: string | null;
  accent?: string | null;
  gender?: string | null;
  language?: string | null;
  languageCode?: string | null;
  provider?: string | null;
}

/**
 * Catalog / PATCH short codes. Backend accepts these and stores the slug
 * (`11L` → `11labs`). Keep `normalizeProviderCode` separate — voice-param
 * schemas still use older codes like `VAPI`.
 */
const VOICE_PROVIDER_CODES = new Set([
  "VP",
  "HU",
  "SAI",
  "11L",
  "LMT",
  "TAV",
  "AZ",
  "NEU",
  "SES",
  "CTS",
  "OAI",
  "INW",
  "CV",
  "PHT",
  "MMX",
  "DG",
  "RME",
  "XAI",
]);

const VOICE_PROVIDER_CODE_ALIASES: Record<string, string> = {
  VAPI: "VP",
  HUME: "HU",
  LMNT: "LMT",
  RIME: "RME",
};

const VOICE_PROVIDER_SLUGS: Record<string, string> = {
  vapi: "VP",
  hume: "HU",
  "smallest-ai": "SAI",
  "11labs": "11L",
  lmnt: "LMT",
  tavus: "TAV",
  azure: "AZ",
  neuphonic: "NEU",
  sesame: "SES",
  cartesia: "CTS",
  openai: "OAI",
  inworld: "INW",
  "custom-voice": "CV",
  playht: "PHT",
  minimax: "MMX",
  deepgram: "DG",
  "rime-ai": "RME",
  rime: "RME",
  xai: "XAI",
};

/** Slug → compact badge code used by voice-param schemas (`VAPI`, `11L`, …). */
const PROVIDER_CODES: Record<string, string> = {
  cartesia: "CTS",
  "11labs": "11L",
  openai: "OAI",
  hume: "HUME",
  hu: "HU",
  azure: "AZ",
  deepgram: "DG",
  lmnt: "LMNT",
  neuphonic: "NEU",
  playht: "PHT",
  "rime-ai": "RIME",
  "smallest-ai": "SAI",
  tavus: "TAV",
  sesame: "SES",
  inworld: "INW",
  minimax: "MMX",
  xai: "XAI",
  vapi: "VAPI",
  vp: "VP",
  "custom-voice": "CV",
};

const PROVIDER_NAMES: Record<string, string> = {
  cartesia: "Cartesia",
  cts: "Cartesia",
  "11labs": "ElevenLabs",
  "11l": "ElevenLabs",
  openai: "OpenAI",
  oai: "OpenAI",
  hume: "Hume",
  hu: "Hume",
  azure: "Azure",
  az: "Azure",
  deepgram: "Deepgram",
  dg: "Deepgram",
  lmnt: "LMNT",
  lmt: "LMNT",
  neuphonic: "Neuphonic",
  neu: "Neuphonic",
  playht: "PlayHT",
  pht: "PlayHT",
  "rime-ai": "Rime AI",
  rime: "Rime AI",
  rme: "Rime AI",
  "smallest-ai": "Smallest AI",
  sai: "Smallest AI",
  tavus: "Tavus",
  tav: "Tavus",
  sesame: "Sesame",
  ses: "Sesame",
  inworld: "Inworld",
  inw: "Inworld",
  minimax: "MiniMax",
  mmx: "MiniMax",
  xai: "xAI",
  vapi: "Vapi",
  vp: "Vapi",
  "custom-voice": "Custom voice",
  cv: "Custom voice",
};

const KNOWN_PROVIDER_CODES = new Set(
  Object.values(PROVIDER_CODES).map((code) => code.toUpperCase()),
);

/** Normalize a raw API provider string to a badge code (`XAI`, `11L`, …). */
export function normalizeProviderCode(provider?: string | null): string {
  if (!provider?.trim()) return "";
  const raw = provider.trim();
  const upper = raw.toUpperCase();
  if (KNOWN_PROVIDER_CODES.has(upper)) return upper;
  const key = raw.toLowerCase();
  return PROVIDER_CODES[key] ?? upper;
}

/** Catalog / PATCH short code (`VP`, `11L`). Accepts slugs and older codes. */
export function toVoiceProviderCode(provider?: string | null): string {
  if (!provider?.trim()) return "";
  const raw = provider.trim();
  const upper = raw.toUpperCase();
  if (VOICE_PROVIDER_CODE_ALIASES[upper]) return VOICE_PROVIDER_CODE_ALIASES[upper];
  if (VOICE_PROVIDER_CODES.has(upper)) return upper;
  return VOICE_PROVIDER_SLUGS[raw.toLowerCase()] ?? upper;
}

/** Filter key for a voice — custom clones bucket under `CV`. */
export function getProviderFilterKey(voice: VoiceOption): string {
  if (voice.is_custom) return "CV";
  return toVoiceProviderCode(voice.provider);
}

/** Badge / filter label for a voice (`CV`, `11L`, `XAI`, …). */
export function getProviderBadgeLabel(voice: VoiceOption): string {
  return getProviderFilterKey(voice);
}

export interface ProviderFilter {
  code: string;
  label: string;
  count: number;
}

/** Provider chips present in the list, most-populated first. */
export function listProviderFilters(voices: VoiceOption[]): ProviderFilter[] {
  const counts = new Map<string, number>();
  for (const voice of voices) {
    const code = getProviderFilterKey(voice);
    if (!code) continue;
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }

  return Array.from(counts, ([code, count]) => ({
    code,
    label: code,
    count,
  })).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function matchesProvider(voice: VoiceOption, providerFilter: string | null): boolean {
  if (!providerFilter) return true;
  return getProviderFilterKey(voice) === providerFilter;
}

/** Human-readable provider name from a slug or code. */
export function getProviderName(provider?: string | null): string {
  if (!provider?.trim()) return "";
  const key = provider.trim().toLowerCase();
  return PROVIDER_NAMES[key] ?? provider.trim();
}

export interface LanguageFilter {
  /** Primary subtag, e.g. `en` */
  code: string;
  /** Display label, e.g. `English` */
  label: string;
  count: number;
}

const languageDisplayNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "language" })
    : null;

const LANGUAGE_NAME_CODES: Record<string, string> = {
  afrikaans: "af",
  albanian: "sq",
  amharic: "am",
  arabic: "ar",
  bengali: "bn",
  bulgarian: "bg",
  catalan: "ca",
  chinese: "zh",
  croatian: "hr",
  czech: "cs",
  danish: "da",
  dutch: "nl",
  english: "en",
  finnish: "fi",
  french: "fr",
  galician: "gl",
  german: "de",
  greek: "el",
  hebrew: "he",
  hindi: "hi",
  hungarian: "hu",
  indonesian: "id",
  italian: "it",
  japanese: "ja",
  korean: "ko",
  malay: "ms",
  mandarin: "zh",
  norwegian: "no",
  polish: "pl",
  portuguese: "pt",
  romanian: "ro",
  russian: "ru",
  serbian: "sr",
  slovak: "sk",
  slovenian: "sl",
  spanish: "es",
  swedish: "sv",
  tagalog: "tl",
  tamil: "ta",
  thai: "th",
  turkish: "tr",
  ukrainian: "uk",
  urdu: "ur",
  vietnamese: "vi",
  welsh: "cy",
  xhosa: "xh",
};

const ENGLISH_ACCENT_TERMS = [
  "american",
  "australian",
  "british",
  "canadian",
  "english",
  "irish",
  "new zealand",
  "scottish",
  "south african",
  "united kingdom",
  "united states",
  "welsh",
  "kiwi",
];

function normalizeLanguageCode(value?: string | null): string {
  const normalized = value?.trim().replaceAll("_", "-").toLowerCase() ?? "";
  if (!normalized) return "";

  const code = normalized.split("-")[0];
  if (/^[a-z]{2,3}$/.test(code)) return code;

  const languageName = normalized.split("(")[0].trim();
  return LANGUAGE_NAME_CODES[languageName] ?? "";
}

function isEnglishAccent(accent?: string | null): boolean {
  const normalized = accent?.trim().toLowerCase() ?? "";
  return ENGLISH_ACCENT_TERMS.some((term) => normalized.includes(term));
}

/** Prefer provider language metadata, inferring English only from known dialect accents. */
export function getVoiceLanguageCode(voice: {
  languageCode?: string | null;
  language?: string | null;
  accent?: string | null;
}): string {
  return (
    normalizeLanguageCode(voice.languageCode) ||
    normalizeLanguageCode(voice.language) ||
    (isEnglishAccent(voice.accent) ? "en" : "")
  );
}

/** Region subtag of a voice, uppercased. Empty when the tag has no region. */
export function getRegionCode(voice: {
  languageCode?: string | null;
  language?: string | null;
}): string {
  const raw = voice.languageCode?.trim() || voice.language?.trim() || "";
  const parts = raw.replaceAll("_", "-").split("-");
  const region = parts.find(
    (part) => /^[a-z]{2}$/i.test(part) || /^\d{3}$/.test(part),
  );
  return region?.toUpperCase() ?? "";
}

/** English name for a primary subtag. Falls back to the raw code. */
export function getLanguageLabel(code: string): string {
  if (!code) return "";
  try {
    return languageDisplayNames?.of(code) ?? code;
  } catch {
    // Intl throws RangeError on tags it cannot parse.
    return code;
  }
}

/** Language chips present in the list, most-populated first. */
export function listLanguageFilters(
  voices: Array<{
    languageCode?: string | null;
    language?: string | null;
    accent?: string | null;
  }>,
): LanguageFilter[] {
  const counts = new Map<string, number>();
  for (const voice of voices) {
    const code = getVoiceLanguageCode(voice);
    if (!code) continue;
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }

  return Array.from(counts, ([code, count]) => ({
    code,
    label: getLanguageLabel(code),
    count,
  })).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function matchesLanguage(
  voice: {
    languageCode?: string | null;
    language?: string | null;
    accent?: string | null;
  },
  languageFilter: string | null,
): boolean {
  if (!languageFilter) return true;
  return getVoiceLanguageCode(voice) === languageFilter;
}

/** Card subtitle fragment: `English (US)`, `English`, or the accent. */
export function getVoiceLocaleLabel(voice: {
  languageCode?: string | null;
  language?: string | null;
  accent?: string | null;
}): string {
  const languageCode = getVoiceLanguageCode(voice);
  const language = languageCode
    ? getLanguageLabel(languageCode)
    : (voice.language?.trim() ?? "");
  if (!language) return voice.accent?.trim() ?? "";
  const region = getRegionCode(voice);
  return region ? `${language} (${region})` : language;
}

/** Search blob used by the voice picker's text filter. */
export function buildVoiceSearchText(voice: VoiceOption): string {
  return [
    voice.name,
    voice.provider,
    getProviderFilterKey(voice),
    getProviderName(voice.provider),
    voice.accent,
    voice.gender,
    voice.language,
    voice.languageCode,
    getLanguageLabel(
      voice.languageCode?.trim().split("-")[0].toLowerCase() ?? "",
    ),
    voice.description,
    voice.is_custom ? "custom" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function toVoiceOption(voice: Voice): VoiceOption {
  return {
    voice_id: voice.voice_id,
    name: voice.name,
    previewUrl: voice.previewUrl ?? null,
    is_custom: Boolean(voice.is_custom),
    description: voice.description ?? null,
    accent: voice.accent ?? null,
    gender: voice.gender ?? null,
    language: voice.language ?? null,
    languageCode: voice.languageCode ?? null,
    provider: voice.provider ?? null,
  };
}

/** Resolve slider increment from params_schema — prefer provider `step`. */
export function getParamStep(
  schema: { type?: string; step?: number | string; min?: number; max?: number },
  paramName?: string,
): number {
  const raw = schema.step;
  const parsed =
    typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;

  if (Number.isFinite(parsed) && parsed > 0) return parsed;

  if (paramName === "speed" && schema.min === -1 && schema.max === 1)
    return 0.1;
  return schema.type === "float" ? 0.1 : 1;
}

/** Snap a value onto the nearest step increment within [min, max]. */
export function snapToStep(
  value: number,
  step: number,
  min?: number,
  max?: number,
): number {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) {
    return value;
  }

  const origin = min ?? 0;
  const snapped = Math.round((value - origin) / step) * step + origin;
  const decimals = countStepDecimals(step);
  const rounded =
    decimals === 0 ? Math.round(snapped) : Number(snapped.toFixed(decimals));

  if (typeof min === "number" && rounded < min) return min;
  if (typeof max === "number" && rounded > max) return max;
  return rounded;
}

export function countStepDecimals(step: number): number {
  if (!Number.isFinite(step) || step >= 1) return 0;
  const asString = step.toString();
  if (asString.includes("e-")) {
    const exp = Number(asString.split("e-")[1]);
    return Number.isFinite(exp) ? exp : 2;
  }
  const parts = asString.split(".");
  return parts[1]?.length ?? 0;
}

export function formatStepValue(value: number, step: number): string {
  return value.toFixed(countStepDecimals(step));
}
